/**
 * eSign API client — fetch wrapper for all eSign endpoints.
 *
 * Authenticated (sender) calls use Firebase ID tokens.
 * Public (signer) calls require no auth — only a signing token.
 */

import { getAuth } from 'firebase/auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// ─── helpers ─────────────────────────────────────────────────────────────────

async function authHeaders() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res;
}

// ─── authenticated (sender) ──────────────────────────────────────────────────

/** Create a new draft envelope from a PDF file. */
export async function createEnvelope(file, subject) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  form.append('subject', subject);
  const res = await apiFetch('/api/envelopes', {
    method: 'POST',
    headers,
    body: form,
  });
  return res.json();
}

/** List envelopes with optional filters (search, sort, status, counts). */
export async function listEnvelopes({ status, q, sort, dir, limit, cursor, counts } = {}) {
  const headers = await authHeaders();
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  if (sort) params.set('sort', sort);
  if (dir) params.set('dir', dir);
  if (limit) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (counts === false) params.set('counts', '0');
  const qs = params.toString();
  const res = await apiFetch(`/api/envelopes${qs ? `?${qs}` : ''}`, { headers });
  return res.json();
}

/** Get a single envelope by ID. */
export async function getEnvelope(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}`, { headers });
  return res.json();
}

/** Update envelope metadata (subject, message, etc.). */
export async function updateEnvelope(id, updates) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return res.json();
}

/** Replace all recipients on an envelope. */
export async function replaceRecipients(id, recipients) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/recipients`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(recipients),
  });
  return res.json();
}

/** Replace all fields on an envelope. */
export async function replaceFields(id, fields) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/fields`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return res.json();
}

/** Send an envelope (transition from draft to sent). */
export async function sendEnvelope(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/send`, {
    method: 'POST',
    headers,
  });
  return res.json();
}

/** Void a sent envelope. */
export async function voidEnvelope(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/void`, {
    method: 'POST',
    headers,
  });
  return res.json();
}

/** Get a pre-signed URL for the original uploaded PDF (download/iframe). */
export async function getOriginalUrl(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/original`, { headers });
  return res.json();
}

/** Fetch the raw PDF bytes via server proxy (no GCS CORS needed). */
export async function getEnvelopeDocument(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/document`, { headers });
  return res.arrayBuffer();
}

/** Get a pre-signed URL for the completed signed PDF. */
export async function getCompletedUrl(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/completed`, { headers });
  return res.json();
}

/** Get a pre-signed URL for the certificate PDF. */
export async function getCertificateUrl(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/certificate`, { headers });
  return res.json();
}

/** Resend the signing invite email to a specific recipient. */
export async function resendInvite(id, recipientId) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/resend`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipientId }),
  });
  return res.json();
}

/** Delete a draft envelope. */
export async function deleteEnvelope(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}`, { method: 'DELETE', headers });
  return res.json();
}

/** Duplicate an envelope into a fresh draft. */
export async function duplicateEnvelope(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/duplicate`, { method: 'POST', headers });
  return res.json();
}

/** Download the audit event CSV for an envelope. */
export async function downloadAuditCsv(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelopes/${id}/audit.csv`, { headers });
  return res.text();
}

/** Get per-account eSign preferences. */
export async function getEsignPreferences() {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/esign/preferences`, { headers });
  return res.json();
}

/** Update per-account eSign preferences (PUT, partial payload supported). */
export async function updateEsignPreferences(patch) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/esign/preferences`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return res.json();
}

// ─── envelope templates ────────────────────────────────────────────────────

/** List envelope templates for the authenticated account. */
export async function listEnvelopeTemplates(q = '') {
  const headers = await authHeaders();
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  const res = await apiFetch(`/api/envelope-templates${qs}`, { headers });
  return res.json();
}

/** Create a template by cloning an existing envelope's doc/roles/fields. */
export async function createTemplateFromEnvelope({ sourceEnvelopeId, name, description }) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      source_envelope_id: sourceEnvelopeId,
      name,
      description,
    }),
  });
  return res.json();
}

/** Create a fresh template from a PDF upload + role definitions. */
export async function createTemplateFromUpload({ file, name, description, roles, subjectTemplate, messageTemplate, routing }) {
  const headers = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  form.append('name', name);
  if (description) form.append('description', description);
  form.append('roles', JSON.stringify(roles));
  if (subjectTemplate) form.append('subject_template', subjectTemplate);
  if (messageTemplate) form.append('message_template', messageTemplate);
  if (routing) form.append('routing', routing);
  const res = await apiFetch(`/api/envelope-templates`, {
    method: 'POST',
    headers,
    body: form,
  });
  return res.json();
}

/** Fetch a single template with its roles + fields. */
export async function getEnvelopeTemplate(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates/${id}`, { headers });
  return res.json();
}

/** Update template metadata (name, description, subject, roles, fields). */
export async function updateEnvelopeTemplate(id, patch) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates/${id}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return res.json();
}

/** Delete a template. */
export async function deleteEnvelopeTemplate(id) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates/${id}`, {
    method: 'DELETE',
    headers,
  });
  return res.json();
}

/** Instantiate a draft envelope from a template with role -> recipient mappings. */
export async function instantiateEnvelopeTemplate(id, { recipients, subject, message, routing }) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates/${id}/instantiate`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipients, subject, message, routing }),
  });
  return res.json();
}

/** Bulk-send a template to a set of rows (each row = one new envelope). */
export async function bulkSendTemplate(id, { rows, sendNow = true }) {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-templates/${id}/bulk-send`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, send_now: sendNow }),
  });
  return res.json();
}

/** List recent bulk-send batches for this account. */
export async function listBulkBatches() {
  const headers = await authHeaders();
  const res = await apiFetch(`/api/envelope-bulk-batches`, { headers });
  return res.json();
}

// ─── public (verification — no auth) ────────────────────────────────────────

/** Public metadata for the verification landing page. */
export async function getPublicEnvelopeMeta(envelopeId) {
  const res = await apiFetch(`/api/envelopes/${envelopeId}/public-meta`);
  return res.json();
}

/** Upload a PDF to verify its SHA-256 against the completed envelope. */
export async function verifyEnvelopePdf(envelopeId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(`/api/envelopes/${envelopeId}/verify`, {
    method: 'POST',
    body: form,
  });
  return res.json();
}

// ─── public (signer — no auth) ──────────────────────────────────────────────

/** Get metadata for a signing session (recipient info, envelope subject, etc.). */
export async function getSignerMeta(token) {
  const res = await apiFetch(`/api/sign/${token}/meta`);
  return res.json();
}

/** Download the PDF document for signing. */
export async function getSignerDocument(token) {
  const res = await apiFetch(`/api/sign/${token}/document`);
  return res.blob();
}

/** Submit completed signature fields. */
export async function submitSignature(token, body) {
  const res = await apiFetch(`/api/sign/${token}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/** Decline to sign. */
export async function declineSignature(token, body) {
  const res = await apiFetch(`/api/sign/${token}/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/** Get a pre-signed URL for the completed (fully signed) PDF. */
export async function getCompletedDownload(token) {
  const res = await apiFetch(`/api/sign/${token}/completed-download`);
  return res.json();
}

/** Get a pre-signed URL for the audit certificate PDF. */
export async function getCertificateDownload(token) {
  const res = await apiFetch(`/api/sign/${token}/certificate`);
  return res.json();
}
